import { Chip } from '@mui/material';
import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { PALETTE, formatBucket } from 'entities/analytics/lib';
import { useActiveUsersTrend } from 'entities/analytics/model';
import { AnalyticsCard } from 'entities/analytics/ui/analytics-card';
import { EChart } from 'entities/analytics/ui/echart';

import { AnalyticsRangeParams } from 'shared/api';

type Props = { params: AnalyticsRangeParams };

export const ActiveUsersTrendCard = ({ params }: Props) => {
  const granularity = params.granularity ?? 'month';
  const { data, isLoading, isFetching } = useActiveUsersTrend(params);
  const series = useMemo(() => data?.data.series ?? [], [data]);

  const option = useMemo<EChartsOption>(
    () => ({
      grid: { left: 8, right: 24, top: 12, bottom: 44, containLabel: true },
      tooltip: { trigger: 'axis' },
      legend: { bottom: 4, textStyle: { color: PALETTE.axis, fontSize: 11 }, itemHeight: 9 },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: series.map((p) => formatBucket(p.bucket, granularity)),
        axisLine: { lineStyle: { color: '#dfe5e3' } },
        axisLabel: { color: PALETTE.axis },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        splitLine: { lineStyle: { color: PALETTE.grid } },
        axisLabel: { color: PALETTE.axis },
      },
      series: [
        {
          name: 'DAU',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: series.map((p) => p.dau),
          lineStyle: { width: 2.5, color: PALETTE.primary },
          itemStyle: { color: PALETTE.primary },
        },
        {
          name: 'WAU',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: series.map((p) => p.wau),
          lineStyle: { width: 2.5, color: PALETTE.blue },
          itemStyle: { color: PALETTE.blue },
        },
        {
          name: 'MAU',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: series.map((p) => p.mau),
          lineStyle: { width: 2.5, color: PALETTE.amber },
          itemStyle: { color: PALETTE.amber },
        },
      ],
    }),
    [series, granularity],
  );

  return (
    <AnalyticsCard
      title="Active users trend"
      subtitle="DAU / WAU / MAU from daily snapshots"
      rightAction={<Chip size="small" label="collecting since deploy" sx={{ bgcolor: '#fff5e0', color: '#b7841c' }} />}
      isLoading={isLoading}
      isFetching={isFetching}
      isEmpty={!isLoading && series.length === 0}
      footer="Snapshots accumulate from the analytics module deploy. Earlier periods may be empty."
      chartHeight={300}
      chart={<EChart option={option} height={300} />}
    />
  );
};
